import { formatDistanceToNow } from 'date-fns';
import prettyBytes from 'pretty-bytes';
import { Download, Package } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { MarkdownBody } from '@/features/issues/components/MarkdownBody';
import { useAuth } from '@/lib/auth/useAuth';

import { buildAssetDownloadUrl, downloadAsset } from '../api';
import type { PortalRelease } from '../types';

interface Props {
  release: PortalRelease;
  expanded?: boolean;
}

// Collapsed view shows the first Markdown paragraph; expanded shows the full
// body. "First paragraph" is whatever appears before the first blank line —
// we don't try to be clever about headings or fenced blocks inside it.
function firstParagraph(body: string): string {
  const trimmed = body.trim();
  if (!trimmed) return '';
  const idx = trimmed.indexOf('\n\n');
  return idx === -1 ? trimmed : trimmed.slice(0, idx);
}

export function ReleaseCard({ release, expanded = false }: Props) {
  const { accessToken } = useAuth();

  const handleDownload = async (assetName: string) => {
    const url = buildAssetDownloadUrl(release.repoName, release.tag, assetName);
    try {
      await downloadAsset(url, assetName, accessToken);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Download failed');
    }
  };

  const bodyToRender = expanded ? release.body : firstParagraph(release.body);

  return (
    <Card data-testid={`release-card-${release.repoName}-${release.tag}`}>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              {release.productDisplayName}
            </div>
            <div className="mt-1 flex items-center gap-2">
              <span className="font-mono text-sm">{release.tag}</span>
              {release.isPrerelease && (
                <Badge data-testid="prerelease-badge" className="bg-amber-100 text-amber-800">
                  Prerelease
                </Badge>
              )}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              Released {formatDistanceToNow(new Date(release.publishedAt), { addSuffix: true })}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div>
          <h3 className="mb-2 text-base font-semibold">{release.name}</h3>
          {bodyToRender.trim() ? (
            <MarkdownBody>{bodyToRender}</MarkdownBody>
          ) : (
            <div className="text-sm text-muted-foreground">No release notes.</div>
          )}
        </div>

        {release.assets.length > 0 && (
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Assets
            </div>
            <ul className="space-y-1">
              {release.assets.map((asset) => (
                <li
                  key={asset.name}
                  className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                  data-testid={`asset-row-${asset.name}`}
                >
                  <div className="flex items-center gap-2 truncate">
                    <Package className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                    <span className="truncate font-mono text-xs">{asset.name}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span>{prettyBytes(asset.sizeBytes)}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(asset.name)}
                      aria-label={`Download ${asset.name}`}
                    >
                      <Download className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                      Download
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
